import React, {
  createContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
  useContext
} from 'react';
import { oneOfType, arrayOf, node, bool, func } from 'prop-types';

// context
import { PopupContentContext } from '../withPopupContent/withPopupContent';

export const LabeledItemsGroupContext = createContext({});

export const LabeledItemsGroup = ({
  children,
  disabled = false,
  ready = null,
  onMaxWidthChange
}) => {
  const { ready: popupContentReady } = useContext(PopupContentContext);

  const firstMount = useRef(true);
  const [labelMaxWidth, setLabelMaxWidth] = useState(0);
  const [groupSubscribers, setGroupSubscribers] = useState([]);

  useEffect(() => {
    if (firstMount.current) {
      firstMount.current = false;
      return;
    }

    if (!onMaxWidthChange) return;

    onMaxWidthChange(labelMaxWidth);
  }, [labelMaxWidth]);

  useEffect(() => {
    const maxWidth = groupSubscribers.reduce((maxItemWidth, { width }) => {
      if (width > maxItemWidth) return width;

      return maxItemWidth;
    }, 0);

    setLabelMaxWidth(maxWidth);
  }, [groupSubscribers]);

  const subscribeToGroup = useCallback(
    ({ labelId, labelWidth }) => {
      setGroupSubscribers((state) => [
        ...state,
        { id: labelId, width: labelWidth }
      ]);
    },
    [setGroupSubscribers]
  );

  const groupSubscribersIds = useMemo(
    () => groupSubscribers.map(({ id }) => id),
    [groupSubscribers]
  );

  const groupReady = useMemo(() => {
    if (ready !== null) return ready;

    if (popupContentReady !== undefined) return popupContentReady;

    return true;
  }, [ready, popupContentReady]);

  if (disabled) return children;

  return (
    <LabeledItemsGroupContext.Provider
      value={{
        subscribeToGroup,
        groupSubscribersIds,
        labelMaxWidth,
        groupReady
      }}
    >
      {children}
    </LabeledItemsGroupContext.Provider>
  );
};

LabeledItemsGroup.propTypes = {
  children: oneOfType([arrayOf(node), node]),
  ready: bool,
  onMaxWidthChange: func,
  disabled: bool
};
